/**
 * Pricing Service - Customer-Specific Pricing Management
 *
 * Simple pricing system:
 * - Base Price (Product MRP)
 * - Customer-Specific Price (overrides base price for specific customers)
 */

import apiService from 'core/services/apiClient';

const API_PATH = 'web/master/product/pricing';

// Interfaces
export interface CustomerSpecialPrice {
  id: string;
  customerId: string;
  productId: string;
  specialPrice: number;
  isActive: boolean;
  product?: {
    id: string;
    productCode: string;
    productName: string;
    mrp: number;
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface AppliedDiscount {
  type: 'product' | 'category' | 'customer';
  name: string;
  levelNumber: number;
  percentage: number;
  amount: number;
  priceAfter: number;
}

export interface PriceCalculationResult {
  basePrice: number;
  appliedPrice: number;
  priceSource: 'base' | 'customer_special' | 'state_price';
  discounts: AppliedDiscount[];
  discountTypesUsed: string[];
  finalPrice: number;
  totalDiscount: number;
  effectiveDiscountPercentage: number;
}

export interface CreateCustomerPriceDto {
  customerId: string;
  productId: string;
  specialPrice: number;
}

class PricingService {
  /**
   * Calculate final price with customer pricing and discounts
   */
  async calculatePrice(
    productId: string,
    customerId: string,
    state?: string,
    customerLoginTypeId?: number
  ): Promise<PriceCalculationResult> {
    const response = await apiService.post(`${API_PATH}/calculate`, {
      productId,
      customerId,
      state,
      customerLoginTypeId,
    });
    return response?.data || response;
  }

  /**
   * Get all customer prices
   */
  async getAllcustomerPrices(): Promise<CustomerSpecialPrice[]> {
    const response = await apiService.get(`${API_PATH}/customer-prices`);
    return response?.data || response || [];
  }

  /**
   * Get customer prices for a specific customer
   */
  async getcustomerPricesByCustomer(customerId: string): Promise<CustomerSpecialPrice[]> {
    const response = await apiService.get(`${API_PATH}/customer-prices/customer/${customerId}`);
    return response?.data || response || [];
  }

  /**
   * Get customer prices for a specific product
   */
  async getcustomerPricesByProduct(productId: string): Promise<CustomerSpecialPrice[]> {
    const response = await apiService.get(`${API_PATH}/customer-prices/product/${productId}`);
    return response?.data || response || [];
  }

  /**
   * Create customer special price
   */
  async createCustomerPrice(data: CreateCustomerPriceDto): Promise<CustomerSpecialPrice> {
    const response = await apiService.post(`${API_PATH}/customer-prices`, data);
    return response?.data || response;
  }

  /**
   * Update customer price
   */
  async updateCustomerPrice(
    id: string,
    data: { specialPrice?: number; isActive?: boolean }
  ): Promise<CustomerSpecialPrice> {
    const response = await apiService.put(`${API_PATH}/customer-prices/${id}`, data);
    return response?.data || response;
  }

  /**
   * Delete customer price
   */
  async deleteCustomerPrice(id: string): Promise<void> {
    await apiService.delete(`${API_PATH}/customer-prices/${id}`);
  }

  /**
   * Toggle customer price active status
   */
  async togglecustomerPriceStatus(id: string, isActive: boolean): Promise<CustomerSpecialPrice> {
    return this.updateCustomerPrice(id, { isActive });
  }
}

export const pricingService = new PricingService();
export default pricingService;
